import * as dayjs from 'dayjs';
import * as _ from 'lodash';

import { ResultDataType } from '../shared';

interface IStoreMapping<TKey extends string | number, TObj> {
    itemIds: TKey[];
    items: {
        [id: number]: TObj;
        [key: string]: TObj;
    };
}

export function asNumber(value: any): number {
    if (!value && value !== 0) {
        return null;
    }

    return isNaN(+value) ? null : +value;
}

export function asDate(value: any): Date {
    if (!value) {
        return null;
    }

    // Parse the value and then convert back to date object
    const date = dayjs(value, 'MM/DD/YYYY');
    return date.isValid() ? date.toDate() : null;
}

export function asDateString(value: string | Date, format: string = 'MM/DD/YYYY'): string {
    const date = asDate(value);
    return date ? dayjs(date).format(format) : null;
}

export function getCharCode(event: JQuery.KeyDownEvent | JQuery.KeyPressEvent): number {
    const ev: any = event || window.event;
    return typeof ev.which === 'undefined' ? ev.keyCode : ev.which;
}

export function isNumber(value: any): boolean {
    return asNumber(value) !== null;
}

export function isDate(value: any): boolean {
    return asDate(value) !== null;
}

export function isCharNumeric(
    event: JQuery.KeyDownEvent | JQuery.KeyPressEvent,
    allowNegative: boolean = true
): boolean {
    const char = getCharCode(event);
    const value = String.fromCharCode(char);
    const regex = allowNegative ? /[\-\.\d]/g : /[\.\d]/g;

    return regex.test(value);
}

export function isEquivalent<TObj>(
    a: TObj,
    b: TObj,
    shouldSkip?: (key: string) => boolean
): boolean {
    if (a && b) {
        for (const key in a) {
            if (a[key] !== b[key]) {
                const skip = shouldSkip ? shouldSkip(key) : false;
                if (!skip) {
                    return false;
                }
            }
        }
    }

    return true;
}

export function getOrdinal(value: number): string {
    if (isNumber(value)) {
        // Calculate the suffix for the number
        const suffix = ['th', 'st', 'nd', 'rd'];
        const index1 = value % 100;
        const index2 = (index1 - 20) % 10;

        return suffix[index2] || suffix[index1] || suffix[0];
    }

    return null;
}

export function getResultDataType(value: number, percent: boolean): ResultDataType {
    switch (value) {
        case 1: {
            return ResultDataType.DOLLAR;
        }
        case 1000: {
            return ResultDataType.THOUSAND_DOLLAR;
        }
        case 1000000: {
            return ResultDataType.MILLION_DOLLAR;
        }
        case 1000000000: {
            return ResultDataType.BILLION_DOLLAR;
        }
        default: {
            return percent ? ResultDataType.PERCENT : ResultDataType.DOLLAR;
        }
    }
}

export function mapArrayForStore<TKey extends string | number, TObj>(
    array: TObj[],
    key: (obj: TObj) => TKey
): IStoreMapping<TKey, TObj> {
    // Get the identifier for each item
    const items = _.map(array, obj => ({
        key: key(obj),
        obj
    }));

    // Convert the array to an object hash
    return {
        itemIds: _.map(items, item => item.key),
        items: _.reduce(
            items,
            (output, item) => ({
                ...output,
                [item.key]: {
                    ...item.obj
                }
            }),
            {}
        )
    };
}

export function enumToValueArray(target: enum): [any] {
    return Object.keys(target).map(key => target[key]);
}
